import { ChevronLeft, ChevronRight } from 'lucide-react'
import { formatNumber, pluralize } from '@/lib/format'
import { Select } from './Input'
import styles from './Pagination.module.css'

interface PaginationProps {
  page: number
  size: number
  totalElements: number
  totalPages: number
  onPageChange: (page: number) => void
  onSizeChange: (size: number) => void
  /** What the rows are, for the summary line: "records", "trips". */
  noun?: string
  /** Only needed when adding an "s" would be wrong — "penalty" -> "penalties". */
  pluralNoun?: string
  disabled?: boolean
}

const PAGE_SIZES = [10, 20, 50, 100] as const

export function Pagination({
  page,
  size,
  totalElements,
  totalPages,
  onPageChange,
  onSizeChange,
  noun = 'record',
  pluralNoun,
  disabled = false,
}: PaginationProps) {
  const first = totalElements === 0 ? 0 : page * size + 1
  const last = Math.min((page + 1) * size, totalElements)

  return (
    // A landmark, so the paging controls are reachable directly rather than by
    // tabbing through every row above them.
    <nav className={styles.pagination} aria-label="Pagination">
      <p className={styles.summary} aria-live="polite">
        {totalElements === 0 ? (
          `No ${pluralize(0, noun, pluralNoun)}`
        ) : (
          <>
            <strong>
              {formatNumber(first)}–{formatNumber(last)}
            </strong>{' '}
            of <strong>{formatNumber(totalElements)}</strong> {pluralize(totalElements, noun, pluralNoun)}
          </>
        )}
      </p>

      <div className={styles.controls}>
        <label className={styles.sizeControl}>
          <span className={styles.sizeLabel}>Rows</span>
          <Select
            value={size}
            onChange={(event) => onSizeChange(Number(event.target.value))}
            disabled={disabled}
            className={styles.sizeSelect}
            aria-label="Rows per page"
          >
            {PAGE_SIZES.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </Select>
        </label>

        <div className={styles.pager}>
          <button
            type="button"
            className={styles.pageButton}
            onClick={() => onPageChange(page - 1)}
            disabled={disabled || page <= 0}
            aria-label="Previous page"
          >
            <ChevronLeft aria-hidden="true" />
          </button>

          <span className={styles.pageIndicator}>
            Page <strong>{formatNumber(page + 1)}</strong> of {formatNumber(Math.max(totalPages, 1))}
          </span>

          <button
            type="button"
            className={styles.pageButton}
            onClick={() => onPageChange(page + 1)}
            disabled={disabled || page + 1 >= totalPages}
            aria-label="Next page"
          >
            <ChevronRight aria-hidden="true" />
          </button>
        </div>
      </div>
    </nav>
  )
}
